import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminHeader from '../../components/AdminHeader';
import { PeriodSelector } from '../../components/AdminPeriodSelector';
import { useAdminPeriod } from '../../components/useAdminPeriod';

type AnuncioRow = {
  ad_key: string;
  ad_name: string;
  leads: number;
  gerados: number;
  pagos: number;
  conversao_pct: number | null;
  gmv: number;
  spend: number;
  cac: number | null;
  roas: number | null;
  ticket_medio: number | null;
  impressions: number;
  clicks: number;
  meta_msgs: number;
};

type CampanhaRow = {
  campaign_key: string;
  campaign: string;
  leads: number;
  gerados: number;
  pagos: number;
  conversao_pct: number | null;
  gmv: number;
  spend: number;
  cac: number | null;
  roas: number | null;
  ticket_medio: number | null;
  impressions: number;
  clicks: number;
  meta_msgs: number;
  anuncios: AnuncioRow[];
};

type CampanhasData = {
  period_days: number;
  production_since: string;
  totals: {
    spend: number;
    gmv: number;
    gmv_sem_atribuicao: number;
    pagos: number;
    pagos_total: number;
    gerados: number;
    leads: number;
    cac: number | null;
    roas: number | null;
  };
  campanhas: CampanhaRow[];
};

const NONE_KEY = '__none__';

function formatBRL(v: number | null | undefined): string {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatBRLn(v: number | null | undefined): string {
  if (v == null) return '—';
  return formatBRL(v);
}

function formatInt(v: number | null | undefined): string {
  return Number(v || 0).toLocaleString('pt-BR');
}

function formatRoas(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${Number(v).toFixed(2)}x`;
}

function formatPctVal(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${Number(v).toFixed(1)}%`;
}

// chaves numéricas ordenáveis da linha
type SortKey =
  | 'campaign'
  | 'spend'
  | 'leads'
  | 'gerados'
  | 'pagos'
  | 'conversao_pct'
  | 'gmv'
  | 'cac'
  | 'roas'
  | 'ticket_medio';

const COLUMNS: { key: SortKey; label: string; align: 'left' | 'right' }[] = [
  { key: 'campaign', label: 'Campanha', align: 'left' },
  { key: 'spend', label: 'Investido', align: 'right' },
  { key: 'leads', label: 'Leads', align: 'right' },
  { key: 'gerados', label: 'Gerados', align: 'right' },
  { key: 'pagos', label: 'Pagos', align: 'right' },
  { key: 'conversao_pct', label: 'Conv.%', align: 'right' },
  { key: 'gmv', label: 'GMV', align: 'right' },
  { key: 'cac', label: 'CAC', align: 'right' },
  { key: 'roas', label: 'ROAS', align: 'right' },
  { key: 'ticket_medio', label: 'Ticket', align: 'right' },
];

export default function Campanhas() {
  const { apiQuery } = useAdminPeriod();

  const [data, setData] = useState<CampanhasData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/admin-dashboard?section=campanhas&${apiQuery}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (err) {
      setError(`Falha ao carregar: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [apiQuery]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'campaign' ? 'asc' : 'desc');
    }
  };

  const totals = data?.totals;

  const sorted = useMemo(() => {
    // comparador genérico — usa `nameKey` ('campaign' p/ campanha, 'ad_name' p/ anúncio)
    // pra a coluna textual; o resto é numérico com null sempre por último.
    const cmp = <T extends Record<string, unknown>>(nameKey: keyof T) => (a: T, b: T) => {
      if (sortKey === 'campaign') {
        const an = String(a[nameKey] ?? '');
        const bn = String(b[nameKey] ?? '');
        return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      }
      const av = a[sortKey as keyof T];
      const bv = b[sortKey as keyof T];
      const an = av == null ? Number.NEGATIVE_INFINITY : Number(av);
      const bn = bv == null ? Number.NEGATIVE_INFINITY : Number(bv);
      return sortDir === 'asc' ? an - bn : bn - an;
    };
    return [...(data?.campanhas ?? [])]
      .map((c) => ({ ...c, anuncios: [...(c.anuncios ?? [])].sort(cmp<AnuncioRow>('ad_name')) }))
      .sort(cmp<CampanhaRow>('campaign'));
  }, [data, sortKey, sortDir]);

  const spendConectado = (totals?.spend ?? 0) > 0;

  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        background: 'var(--cream)',
        color: 'var(--text-body)',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AdminHeader title="Campanhas" />

      <main
        style={{
          flex: 1,
          padding: '20px 16px 60px',
          maxWidth: 1280,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--heading-ink)',
            }}
          >
            Campanhas (Meta Ads)
          </h1>
          <PeriodSelector />
        </div>

        {loading && !data && <SkeletonGrid />}

        {error && (
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              background: 'rgba(220,80,80,0.08)',
              border: '1px solid rgba(220,80,80,0.30)',
              color: '#a93232',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            <p style={{ margin: '0 0 12px', fontSize: 14 }}>{error}</p>
            <button
              onClick={() => {
                setLoading(true);
                load();
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: 'none',
                background: 'var(--gold)',
                color: 'var(--gold-ink)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {data && (
          <>
            {!spendConectado && (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: 12,
                  background: 'var(--gold-pale)',
                  border: '1px solid var(--gold-border)',
                  color: 'var(--amber-deep)',
                  fontSize: 13,
                  marginBottom: 16,
                  lineHeight: 1.5,
                }}
              >
                <strong>Gasto do Meta ainda não conectado.</strong> Os números de funil
                (leads, gerados, pagos, GMV) já são reais; <strong>CAC e ROAS</strong> aparecem
                como “—” até o token <code>ads_read</code> ser conectado e o sync diário rodar.
                As campanhas abaixo aparecem pelo ID do anúncio até os nomes serem sincronizados.
              </div>
            )}

            <CardGrid>
              <KpiCard label="Investimento total" value={formatBRL(totals?.spend)} hint="gasto Meta no período" />
              <KpiCard
                label="GMV atribuído"
                value={formatBRL(totals?.gmv)}
                hint={`+ ${formatBRL(totals?.gmv_sem_atribuicao)} sem atribuição`}
                accentGold
              />
              <KpiCard label="ROAS geral" value={formatRoas(totals?.roas)} accent="green" />
              <KpiCard
                label="CAC médio"
                value={formatBRLn(totals?.cac)}
                hint={`${formatInt(totals?.pagos)} pagos atribuídos`}
              />
              <KpiCard
                label="Leads / Pagos"
                value={`${formatInt(totals?.leads)} / ${formatInt(totals?.pagos_total)}`}
                hint={`${formatInt(totals?.gerados)} pedidos gerados`}
              />
            </CardGrid>

            <SectionTitle>Performance por campanha</SectionTitle>
            <Card>
              {sorted.length === 0 ? (
                <EmptyMsg>Nenhuma campanha no período.</EmptyMsg>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
                    <thead>
                      <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                        {COLUMNS.map((col) => {
                          const isActive = sortKey === col.key;
                          return (
                            <th
                              key={col.key}
                              onClick={() => toggleSort(col.key)}
                              style={{
                                textAlign: col.align,
                                padding: '6px 8px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                                color: isActive ? 'var(--text-strong)' : 'var(--text-muted)',
                              }}
                              title="Clique pra ordenar"
                            >
                              {col.label}
                              {isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.flatMap((c) => {
                        const semAtrib = c.campaign_key === NONE_KEY;
                        const hasAds = !semAtrib && (c.anuncios?.length ?? 0) > 0;
                        const isOpen = expanded.has(c.campaign_key);
                        const rows: React.ReactNode[] = [
                          <tr
                            key={c.campaign_key}
                            onClick={hasAds ? () => toggleExpand(c.campaign_key) : undefined}
                            style={{
                              borderTop: '1px solid var(--border-fade)',
                              opacity: semAtrib ? 0.72 : 1,
                              cursor: hasAds ? 'pointer' : 'default',
                            }}
                          >
                            <td
                              style={{
                                padding: '9px 8px',
                                fontWeight: 700,
                                color: semAtrib ? 'var(--text-muted)' : 'var(--heading-ink)',
                                fontStyle: semAtrib ? 'italic' : 'normal',
                                maxWidth: 240,
                              }}
                              title={c.campaign}
                            >
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                <span
                                  style={{
                                    flexShrink: 0,
                                    width: 14,
                                    fontSize: 10,
                                    color: 'var(--text-muted)',
                                    visibility: hasAds ? 'visible' : 'hidden',
                                  }}
                                >
                                  {isOpen ? '▼' : '▶'}
                                </span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {c.campaign}
                                </span>
                                {hasAds && (
                                  <span
                                    style={{
                                      flexShrink: 0,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: 'var(--text-muted)',
                                      background: 'var(--badge-bg)',
                                      borderRadius: 999,
                                      padding: '0 7px',
                                    }}
                                  >
                                    {c.anuncios.length} {c.anuncios.length === 1 ? 'anúncio' : 'anúncios'}
                                  </span>
                                )}
                              </span>
                            </td>
                            <MetricCells r={c} />
                          </tr>,
                        ];

                        if (hasAds && isOpen) {
                          for (const a of c.anuncios) {
                            rows.push(
                              <tr
                                key={`${c.campaign_key}::${a.ad_key}`}
                                style={{ borderTop: '1px solid var(--border-fade)', background: 'var(--badge-bg)' }}
                              >
                                <td
                                  style={{
                                    padding: '7px 8px 7px 28px',
                                    fontWeight: 600,
                                    fontSize: 12,
                                    color: 'var(--text-strong)',
                                    maxWidth: 240,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={a.ad_name}
                                >
                                  ↳ {a.ad_name}
                                </td>
                                <MetricCells r={a} compact />
                              </tr>
                            );
                          }
                        }

                        return rows;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '14px 2px 0', lineHeight: 1.6 }}>
              <strong>Clique numa campanha</strong> pra abrir os anúncios que a compõem (cada linha = SOMA dos seus anúncios).{' '}
              <strong>Leads</strong> = conversas iniciadas a partir do anúncio (telefones distintos).{' '}
              <strong>GMV/CAC/ROAS</strong> usam a receita real pós-cupom. ROAS e CAC dos totais
              consideram só GMV atribuído. Em janelas curtas (7d) o ROAS sai sub-reportado: o gasto
              é do dia, mas o pagamento (PIX/boleto) costuma chegar dias depois — prefira 30d pra
              leitura de retorno. A linha <em>“(sem atribuição)”</em> agrupa pedidos sem ad_id
              (cliente sem clique rastreável / orgânico).
            </p>
          </>
        )}
      </main>
    </div>
  );
}

// ----- Sub-componentes (mesmo padrão de Dashboard.tsx) -----

// As 9 colunas numéricas (após o nome) — compartilhadas entre linha de campanha e de anúncio.
function MetricCells({ r, compact }: { r: CampanhaRow | AnuncioRow; compact?: boolean }) {
  const roasColor = r.roas == null ? 'var(--text-muted)' : r.roas >= 1 ? '#2f7a4a' : '#a93232';
  const pad = compact ? '7px 8px' : '9px 8px';
  const fz = compact ? 12 : undefined;
  return (
    <>
      <td style={{ padding: pad, textAlign: 'right', fontWeight: 600, fontSize: fz }}>
        {r.spend > 0 ? formatBRL(r.spend) : '—'}
      </td>
      <td style={{ padding: pad, textAlign: 'right', fontSize: fz }}>{formatInt(r.leads)}</td>
      <td style={{ padding: pad, textAlign: 'right', fontSize: fz }}>{formatInt(r.gerados)}</td>
      <td style={{ padding: pad, textAlign: 'right', fontSize: fz }}>{formatInt(r.pagos)}</td>
      <td style={{ padding: pad, textAlign: 'right', fontSize: fz }}>{formatPctVal(r.conversao_pct)}</td>
      <td style={{ padding: pad, textAlign: 'right', fontWeight: 600, fontSize: fz }}>{formatBRL(r.gmv)}</td>
      <td style={{ padding: pad, textAlign: 'right', fontSize: fz }}>{formatBRLn(r.cac)}</td>
      <td style={{ padding: pad, textAlign: 'right', fontWeight: 700, color: roasColor, fontSize: fz }}>
        {formatRoas(r.roas)}
      </td>
      <td style={{ padding: pad, textAlign: 'right', fontSize: fz }}>{formatBRLn(r.ticket_medio)}</td>
    </>
  );
}

function CardGrid({ children, mt = 0 }: { children: React.ReactNode; mt?: number }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginTop: mt,
      }}
    >
      {children}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="admin-glass" style={{ background: 'var(--surface-white)', borderRadius: 18, padding: 16 }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: 'var(--heading-ink)',
        margin: '24px 0 8px',
      }}
    >
      {children}
    </h2>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
      {children}
    </p>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  accentGold,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'green' | 'orange' | 'red';
  accentGold?: boolean;
}) {
  const accentColor =
    accent === 'green'
      ? 'var(--kpi-revenue)'
      : accent === 'orange'
      ? '#cc6620'
      : accent === 'red'
      ? '#a93232'
      : 'var(--heading-ink)';
  return (
    <div
      className={accentGold ? 'admin-glass admin-accent-gold' : 'admin-glass'}
      style={{
        background: 'var(--surface-white)',
        borderRadius: 18,
        padding: '17px 19px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minHeight: 92,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: accentColor, lineHeight: 1.1 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            height: 92,
            background: 'var(--badge-bg)',
            borderRadius: 18,
            border: '1px solid var(--border-soft)',
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  );
}
